/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */
import React, { useState, useEffect } from "react";
import Api from "../../Controllers/Api";
import { buildRegionUrl } from "../../utils.js";
import config from "../../settings.js";
import IndicatorHistoryLineBarChart from "./IndicatorHistoryLineBarChart";
import MultiSelectLegend from "./MultiSelectLegend";
import LoadingOverlay from "../Utils/loadingOverlay";

/** @import {LegendOption} from './MultiSelectLegend' */

/** @import {IndicatorHistoryDataApiResponse} from '../../types/apiTypes' */
/** @import {IndicatorCategoriesValues} from '../../Controllers/types.js' */
/** @import {IndicatorData} from './IndicatorHistoryLineBarChart' */

/**
 * Callback for handling filter changes.
 * @callback OnFilterChange
 * @param {Object} filterChange
 * @param {string} filterChange.category - The name of the category being filtered.
 * @param {Set<string>} filterChange.enable - The list of filters to enable for the category.
 * @param {Set<string>} filterChange.disable - The list of filters to disable for the category.
 * @returns {void}
 */

/**
 *
 * @param {object} props
 * @param {any} props.parentApi
 * @param {number} props.indicatorId
 * @param {string} props.zoneType
 * @param {number} props.zoneId
 * @param {string} props.zoneSubdivision
 * @param {import("../../types/apiTypes").IndicatorChartConfig} props.chartConfig
 * @param {IndicatorCategoriesValues|undefined} [props.filters]
 * @param {OnFilterChange} [props.onFilterChange] - Callback for handling filter changes.
 * @param {number|undefined} props.unitId
 *
 * @returns {JSX.Element}
 */
function IndicatorHistoryRepresentation(_ref) {
  var _chartConfig$chart_op, _chartConfig$chart_op2;
  let {
    parentApi,
    indicatorId,
    zoneType,
    zoneId,
    zoneSubdivision,
    chartConfig,
    filters: filtersFromProps,
    onFilterChange,
    unitId
  } = _ref;
  if (filtersFromProps === undefined) {
    throw Error("filtersFromProps are undefined");
  }
  if (filtersFromProps[chartConfig.categorie] === undefined) {
    throw Error("The filters don't match with the chartConfig. The filter and current indicator states might be out of sync ?");
  }

  // TODO : when uncontrolled : init filters. using initFiltersByCategory ?
  const [filters, setFilters] = useState(filtersFromProps);
  const [legendOptions, setLegendOptions] = useState(/** @type {LegendOption[] | null} */null);
  const [indicatorData, setIndicatorData] = useState(/** @type {IndicatorData | null} */null);
  const [isFetchingData, setIsFetchingData] = useState(false);

  // keep internal filter state in sync with filtersFromProps

  useEffect(() => {
    setFilters(filtersFromProps);
  }, [filtersFromProps]);

  /**
   * Fetches the history data.
   *
   * This component is responsible for independently fetching it's own data.
   */
  useEffect(() => {
    setIsFetchingData(true);

    /**
     * @param {IndicatorHistoryDataApiResponse} data
     * @returns {{ indicatorData: IndicatorData, legendOptions: LegendOption[] }}
     */
    const processIndicatorDataResponse = data => {
      var _analyse$chartConfig$, _analyse$chartConfig$2;
      const analyse = data.analyse || {};
      const categoryData = ((_analyse$chartConfig$ = analyse[chartConfig.categorie]) === null || _analyse$chartConfig$ === void 0 ? void 0 : _analyse$chartConfig$.traj) || [];
      const categories = ((_analyse$chartConfig$2 = analyse[chartConfig.categorie]) === null || _analyse$chartConfig$2 === void 0 ? void 0 : _analyse$chartConfig$2.categories) || [];
      const legendOptions = categories.map(_ref2 => {
        let {
          nom,
          couleur,
          confidentiel
        } = _ref2;
        /** @type {LegendOption} */
        const option = {
          value: nom,
          label: nom,
          color: couleur,
          disabled: confidentiel === "oui"
        };
        return option;
      });

      /** @type {IndicatorData} */
      const indicatorData = {
        unit: data.unite,
        categories: [{
          name: chartConfig.categorie,
          categories: categoryData.map(optionData => {
            return {
              name: optionData.nom,
              data: optionData.data.map(_ref3 => {
                let {
                  annee: year,
                  valeur: value
                } = _ref3;
                return {
                  year,
                  value
                };
              }),
              color: optionData.couleur,
              confidential: optionData.confidentiel === "oui"
            };
          })
        }]
      };
      return {
        indicatorData,
        legendOptions
      };
    };
    const endpointUrl = config.api_analysis_meta_url + `${indicatorId}/graphique/bar`;
    const queryParams = new URLSearchParams({
      zone: zoneType,
      maille: zoneSubdivision,
      zone_id: zoneId.toString()
    });
    if (unitId) {
      queryParams.append("unit_id", unitId.toString());
    }
    const url = `${endpointUrl}?${queryParams.toString()}`;
    const body = JSON.stringify(filters);
    const apiPromise = Api.callApi(buildRegionUrl(url, parentApi.data.region), body, "POST");
    apiPromise.then((/** @type {IndicatorHistoryDataApiResponse} */data) => {
      const {
        indicatorData,
        legendOptions
      } = processIndicatorDataResponse(data);
      setIndicatorData(indicatorData);
      setLegendOptions(prevLegendOptions => {
        // Only set the legend the first time.
        // because legend options are based on API response and response
        // doesn't contain options when no checkboxes are selected (maybe fix the API?)
        if (!prevLegendOptions) {
          return legendOptions;
        }
        return prevLegendOptions;
      });
      setIsFetchingData(false);
    }).catch(error => {
      if (error.name === "AbortError") return;
      setIsFetchingData(false);
      throw error;
    });
    return () => apiPromise.abort();
  }, [zoneType, zoneId, zoneSubdivision, filters, indicatorId, unitId, parentApi.data.region, chartConfig.categorie]);

  /**
   * @param {object} params
   * @param {string} params.categoryName
   * @param {Set<LegendOption["value"]>} params.enable
   * @param {Set<LegendOption["value"]>} params.disable
   */
  const handleFilterChange = _ref4 => {
    let {
      categoryName,
      enable,
      disable
    } = _ref4;
    if (enable.intersection(disable).size > 0) {
      throw new Error(`Error: enable and disable sets cannot have common elements. Overlapping values: ${enable.intersection(disable)}`);
    }
    if (onFilterChange) {
      onFilterChange({
        category: categoryName,
        enable: enable,
        disable: disable
      }); // Controlled behaviour
    } else {
      // TODO : handle uncontrolled behaviour for use in dashboards.
      // setFilters((f) => ({
      //     ...f,
      //     ...updatedCategoryFilters,
      // })); // Uncontrolled filters behaviour
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap align-items-center gap-3"
  }, indicatorData ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LoadingOverlay, {
    isLoading: isFetchingData
  }, /*#__PURE__*/React.createElement(IndicatorHistoryLineBarChart, {
    indicatorData: indicatorData,
    chartOptions: {
      title: chartConfig.titre,
      type: (_chartConfig$chart_op = chartConfig.chart_options) === null || _chartConfig$chart_op === void 0 ? void 0 : _chartConfig$chart_op.type,
      stacked: (_chartConfig$chart_op2 = chartConfig.chart_options) === null || _chartConfig$chart_op2 === void 0 ? void 0 : _chartConfig$chart_op2.stacked
    }
  })), legendOptions.length > 1 && /*#__PURE__*/React.createElement(MultiSelectLegend, {
    options: legendOptions,
    selectedOptions: new Set(filters[chartConfig.categorie].map(f => {
      return f.filtre_categorie.split(".")[1];
    })),
    onChange: selectedOptions => {
      handleFilterChange({
        categoryName: chartConfig.categorie,
        enable: selectedOptions,
        disable: new Set(legendOptions.map(o => o.value)).difference(selectedOptions)
      });
    }
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "spinner-border spinner-border",
    role: "status",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "visually-hidden"
  }, "Loading...")));
}
export default IndicatorHistoryRepresentation;